import { Skeleton } from 'antd';
import type { BasicDataNode, DataNode, TreeProps } from 'antd/es/tree';
import type { CheckInfo } from 'rc-tree/lib/Tree';
import type { ChangeEvent, Key, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { NO_RESULTS, SELECT_ALL_KEY } from './constants';
import * as Styled from './styles';

export interface Props extends TreeProps {
  onSelect?: (selectedKeys: Key[]) => void;
  searchPlaceholder?: string;
  isLoading?: boolean;
  showSelectAll?: boolean;
  selectAllLabel?: string;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  showSearch?: boolean;
  renderContent?: (searchValue: string) => ReactNode;
  filterTreeData?: boolean;
}

const SearchTree = ({
  treeData,
  selectedKeys,
  onSelect,
  searchPlaceholder = 'Search',
  isLoading,
  showSelectAll = false,
  selectAllLabel = 'Select all',
  searchValue,
  onSearchValueChange,
  showSearch = true,
  renderContent,
  checkable = true,
  filterTreeData = true,
  titleRender,
  defaultExpandAll,
  expandedKeys,
  selectable = false,
  showLine = { showLeafIcon: false },
  switcherIcon,
  className
}: Props) => {
  const [checkedKeys, setCheckedKeys] = useState<Key[]>([]);
  const [internalSearchValue, setInternalSearchValue] = useState('');
  const currentSearchValue = searchValue ?? internalSearchValue;

  useEffect(() => {
    if (selectedKeys) {
      setCheckedKeys(selectedKeys);
    }
  }, [selectedKeys]);

  const filteredTreeData: DataNode[] = useMemo(() => {
    const filteredResults = filterTreeData
      ? treeData?.filter(item =>
          (item.title as string)
            .toLowerCase()
            .includes(currentSearchValue.toLowerCase())
        )
      : treeData;
    const showSelectAllAndHasResults =
      showSelectAll && filteredResults?.[0]?.key !== NO_RESULTS;

    if (!!treeData?.length && !filteredResults?.length) {
      return [
        {
          title: 'No results found',
          key: NO_RESULTS,
          checkable: false,
          disabled: true
        }
      ];
    }

    if (!filteredResults?.length) {
      return [];
    }

    if (showSelectAllAndHasResults) {
      return [
        {
          title: selectAllLabel,
          key: SELECT_ALL_KEY,
          checkable: true
        },
        ...filteredResults
      ];
    }

    return filteredResults;
  }, [
    treeData,
    currentSearchValue,
    showSelectAll,
    selectAllLabel,
    filterTreeData
  ]);

  const treeCheckedKeys = useMemo(() => {
    let checked = [...checkedKeys];
    const halfChecked: Key[] = [];

    const realItems = filteredTreeData.filter(
      item => item.key !== SELECT_ALL_KEY && item.key !== NO_RESULTS
    );

    const selectedRealItems = checked.filter(key =>
      realItems.some(item => item.key === key)
    );

    const isAllSelected =
      selectedRealItems.length === realItems.length && realItems.length > 0;

    if (!isAllSelected && checked.length > 0) {
      checked = checked.filter(key => key !== SELECT_ALL_KEY);
      halfChecked.push(SELECT_ALL_KEY);
    }

    if (isAllSelected && !checked.includes(SELECT_ALL_KEY)) {
      checked.push(SELECT_ALL_KEY);
    }

    return { checked, halfChecked };
  }, [checkedKeys, filteredTreeData]);

  const handleOnCheck = useCallback(
    (checked: Key[], { node }: CheckInfo<BasicDataNode | DataNode>) => {
      if (node.key === SELECT_ALL_KEY) {
        const allKeys = filteredTreeData
          .map(item => item.key)
          .filter(key => SELECT_ALL_KEY !== key);
        const isSomeSelected = checkedKeys.length > 0;

        if (isSomeSelected) {
          setCheckedKeys([]);
          onSelect?.([]);

          return;
        }

        const newCheckedKeys = [...new Set([...checkedKeys, ...allKeys])];

        setCheckedKeys(newCheckedKeys);
        onSelect?.(newCheckedKeys);

        return;
      }

      if (currentSearchValue) {
        const filteredKeys = node.checked
          ? checkedKeys.filter(key => key !== node.key)
          : [...checkedKeys, node.key];

        setCheckedKeys(filteredKeys);
        onSelect?.(filteredKeys);

        return;
      }

      const realCheckedKeys = checked.filter(
        key => key !== SELECT_ALL_KEY && key !== NO_RESULTS
      );

      setCheckedKeys(realCheckedKeys);
      onSelect?.(realCheckedKeys);
    },
    [checkedKeys, onSelect, currentSearchValue, filteredTreeData]
  );

  const handleTreeCheck = useCallback(
    (
      checked: Key[] | { checked: Key[]; halfChecked: Key[] },
      info: CheckInfo<BasicDataNode | DataNode>
    ) => {
      const checkedList = showSelectAll
        ? (checked as { halfChecked: Key[]; checked: Key[] }).checked
        : (checked as Key[]);

      handleOnCheck(checkedList, info);
    },
    [showSelectAll, handleOnCheck]
  );

  const handleSearchChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      if (searchValue === undefined) {
        setInternalSearchValue(target.value);
      }
      onSearchValueChange?.(target.value);
    },
    [searchValue, onSearchValueChange]
  );

  const renderTitle = useCallback(
    (node: BasicDataNode | DataNode) =>
      'key' in node ? titleRender?.(node) : null,
    [titleRender]
  );

  return (
    <>
      {showSearch && (
        <Styled.Search
          placeholder={searchPlaceholder}
          disabled={
            isLoading || (filterTreeData && !treeData?.length && !renderContent)
          }
          value={currentSearchValue}
          onChange={handleSearchChange}
          allowClear
        />
      )}
      {renderContent ? (
        renderContent(currentSearchValue)
      ) : (
        <>
          {isLoading && (
            <Skeleton active paragraph={{ rows: 3 }} title={false} />
          )}
          {!isLoading && !!treeData && (
            <Styled.Tree
              className={className}
              treeData={filteredTreeData}
              checkedKeys={treeCheckedKeys}
              onCheck={handleTreeCheck}
              showLine={showLine}
              selectable={selectable}
              selectedKeys={selectable ? selectedKeys : undefined}
              onSelect={selectable ? onSelect : undefined}
              defaultExpandAll={defaultExpandAll}
              expandedKeys={expandedKeys}
              switcherIcon={switcherIcon}
              titleRender={titleRender ? renderTitle : undefined}
              checkStrictly={showSelectAll}
              checkable={checkable}
            />
          )}
        </>
      )}
    </>
  );
};

export default SearchTree;
